import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { useIsAccountOwner } from './auth0-claims';
import { AUTH0_MFA_TYPES, PHONE_VERIFICATION_STATES } from '../authentication/constants';
import { UserSettings } from '../enums/user-settings/user-settings';
import { useStepUpAuthContext } from '../global/step-up-authentication';
import { CHALLENGE_TYPES, MANAGEMENT_TYPES } from '../global/step-up-authentication/constants';
import { CONSENT_MESSAGES } from '../pages/settings/sign-in-and-security/sections/security/add-mfa-enrollment/consent-types';
import { getEnrollmentName } from '../pages/settings/sign-in-and-security/sections/security/add-mfa-enrollment/helpers';
import { startNewMfaVerification } from '../pages/settings/sign-in-and-security/sections/security/api';
import { messages } from '../pages/settings/sign-in-and-security/sections/security/messages';
import Global from '../store/global-selectors';
import { saveUserSetting } from '../store/user/user.actions';

const PHASES = {
  selectType: 'selectType',
  start: 'start',
  challenge: 'challenge',
  success: 'success',
};

/**
 * @typedef {keyof typeof MANAGEMENT_TYPES} ManagementType
 * @typedef {keyof typeof AUTH0_MFA_TYPES} Auth0MfaType
 * @typedef {keyof typeof PHONE_VERIFICATION_STATES} PhoneVerificationState
 */

/**
 * @param {string} countryCode
 * @param {string} phoneNumber
 * @returns {string}
 */
const createCompletePhoneNumber = (countryCode, phoneNumber) => {
  return `+${countryCode}${phoneNumber}`;
};

/**
 * @typedef {object} MfaEnrollmentHookReturn
 * @property {keyof typeof PHASES} phase - Current enrollment phase
 * @property {ManagementType|null} mfaType - Selected MFA type
 * @property {object|null} error - Current error state
 * @property {Auth0MfaType} verificationMethod - Current verification method (sms/voice)
 * @property {string} enrollmentValue - Current enrollment input value
 * @property {object} countryCode - Selected country code object
 * @property {Array<{key: string, text: string, accepted: boolean}>|undefined} consents - Consent data array
 * @property {string} oobCode - Out-of-band code for verification
 * @property {string} enrollmentName - Display name for enrollment
 * @property {string} otpSecret - TOTP secret for authenticator apps
 * @property {string} secretAsQrCodeUri - QR code URI for TOTP setup
 * @property {object} challengedEnrollment - Enrollment object for challenge phase
 * @property {string} accessToken - Step-up auth access token
 * @property {() => void} resetError - Function to clear error state
 * @property {() => void} onCloseModal - Function to close modal
 * @property {(type: ManagementType) => void} onSelectMfaType - Function to select MFA type
 * @property {() => void} advanceToChallengePhase - Function to advance to challenge phase
 * @property {(key: string, accepted: boolean) => void} onConsentChange - Function to handle consent changes
 * @property {() => Promise<void>} onStartVerification - Function to start verification process
 * @property {() => void} onChallengePassed - Function called when challenge is passed
 * @property {(method: Auth0MfaType) => void} setVerificationMethod - Function to set verification method
 * @property {(value: string) => void} setEnrollmentValue - Function to set enrollment value
 * @property {(code: object) => void} setCountryCode - Function to set country code
 * @property {(countryCode: string, phoneNumber: string) => string} createCompletePhoneNumber - Function to create complete phone number
 * @property {typeof PHASES} PHASES - Phase constants object
 * @property {ManagementType|null} currentMfaType - Current MFA type for switching
 * @property {(newType: ManagementType) => void} switchMfaType - Function to switch MFA type
 * @property {boolean} isUnskippable - Whether modal is in unskippable state
 */

/**
 * Shared hook for MFA enrollment logic
 * @param {object} options
 * @param {ManagementType|null} options.restrictToMfaType - Restrict to specific MFA type or null for all
 * @param {() => void} options.onSuccess - Called when enrollment succeeds
 * @param {() => void} options.onClose - Called when modal should close
 * @param {boolean} options.isUnskippable - Whether modal is in unskippable state
 * @param {boolean} options.allowMfaTypeSwitching - Whether MFA type switching is allowed
 * @param {ManagementType|null} options.initialMfaType - Initial MFA type for switching
 * @returns {MfaEnrollmentHookReturn} MFA enrollment state and functions
 */
export const useMfaEnrollment = ({
  restrictToMfaType = null,
  onSuccess,
  onClose,
  isUnskippable = false,
  allowMfaTypeSwitching = false,
  initialMfaType = null,
}) => {
  const isOwner = useIsAccountOwner();
  const isSystemAdmin = useSelector(Global.selectUserisSysAdmin);

  const dispatch = useDispatch();
  const intl = useIntl();
  const { accessToken, detectAndHandleAuthTokenExpirationError } = useStepUpAuthContext();

  // Determine initial phase based on restriction
  const initialPhase = restrictToMfaType ? PHASES.start : PHASES.selectType;

  // State management
  const [mfaType, setMfaType] = useState(restrictToMfaType || null);
  const [phase, setPhase] = useState(initialPhase);
  const [error, setError] = useAsyncSafeState(null);
  const [verificationMethod, setVerificationMethod] = useState(AUTH0_MFA_TYPES.sms);
  const [currentMfaType, setCurrentMfaType] = useState(restrictToMfaType || initialMfaType);

  // Form state
  const [enrollmentValue, setEnrollmentValue] = useState('');
  const [countryCode, setCountryCode] = useState({});
  const [consents, setConsents] = useState(undefined);

  // Challenge state
  const [oobCode, setOobCode] = useState('');
  const [enrollmentName, setEnrollmentName] = useState('');
  const [otpSecret, setOtpSecret] = useState('');
  const [secretAsQrCodeUri, setSecretAsQrCodeUri] = useState('');

  // Callbacks
  const resetError = useCallback(() => setError(null), [setError]);

  const resetState = useCallback(() => {
    resetError();
    setPhase(initialPhase);
    setEnrollmentValue('');
    setCountryCode({});
    setEnrollmentName('');
    setConsents(undefined);
    setOobCode('');
    setOtpSecret('');
    setSecretAsQrCodeUri('');
    if (!restrictToMfaType) {
      setMfaType(null);
    }
  }, [resetError, initialPhase, restrictToMfaType]);

  const onCloseModal = useCallback(() => {
    if (isUnskippable && !allowMfaTypeSwitching) {
      return; // Prevent closing in unskippable state
    }
    resetState();
    onClose();
  }, [isUnskippable, allowMfaTypeSwitching, resetState, onClose]);

  const resetFormState = useCallback(() => {
    resetError();
    setEnrollmentValue('');
    setCountryCode({});
    setEnrollmentName('');
    // Don't reset consents when switching MFA types - preserve user consent choices
    setOobCode('');
    setOtpSecret('');
    setSecretAsQrCodeUri('');
  }, [resetError]);

  const switchMfaType = useCallback(
    (newType) => {
      if (!allowMfaTypeSwitching) return;

      resetFormState();
      setCurrentMfaType(newType);
      setMfaType(newType);
      setPhase(PHASES.start);
    },
    [allowMfaTypeSwitching, resetFormState],
  );

  const onSelectMfaType = useCallback((type) => {
    setMfaType(type);
    setPhase(PHASES.start);
  }, []);

  const advanceToChallengePhase = useCallback(() => {
    setPhase(PHASES.challenge);
  }, []);

  const onConsentChange = useCallback((key, accepted) => {
    setConsents((prevConsents) => {
      if (!prevConsents) {
        // If consents is undefined, create a new consent array with the clicked consent
        return [
          {
            key,
            text: messages.phoneConsentCustomerServiceAndSupportCheckboxLabel,
            accepted,
          },
        ];
      }
      return prevConsents.map((consent) => (consent.key === key ? { ...consent, accepted } : consent));
    });
  }, []);

  const onStartVerification = useCallback(async () => {
    const body = {
      mfaToken: accessToken,
      type: CHALLENGE_TYPES.OOB,
    };

    const activeMfaType = mfaType || restrictToMfaType;

    if (activeMfaType === AUTH0_MFA_TYPES.email) {
      body.oobChannel = AUTH0_MFA_TYPES.email;
      body.email = enrollmentValue;
    } else if (activeMfaType === AUTH0_MFA_TYPES.totp) {
      body.type = CHALLENGE_TYPES.OTP;
    } else if (verificationMethod === AUTH0_MFA_TYPES.sms) {
      body.oobChannel = AUTH0_MFA_TYPES.sms;
      body.phone = createCompletePhoneNumber(countryCode.code, enrollmentValue);
    } else if (verificationMethod === AUTH0_MFA_TYPES.voice) {
      body.oobChannel = AUTH0_MFA_TYPES.voice;
      body.phone = createCompletePhoneNumber(countryCode.code, enrollmentValue);
    }

    try {
      const resData = await startNewMfaVerification(body);

      if (activeMfaType === AUTH0_MFA_TYPES.totp) {
        setOtpSecret(resData.secret);
        setSecretAsQrCodeUri(resData.barcode_uri);
      } else {
        setEnrollmentName(getEnrollmentName(activeMfaType, enrollmentValue, countryCode));
        setOobCode(resData.oob_code);
        advanceToChallengePhase();
      }
    } catch (err) {
      detectAndHandleAuthTokenExpirationError(err);
      setError(err);
    }
  }, [
    accessToken,
    mfaType,
    restrictToMfaType,
    enrollmentValue,
    verificationMethod,
    countryCode,
    advanceToChallengePhase,
    setError,
    detectAndHandleAuthTokenExpirationError,
  ]);

  const challengedEnrollment = useMemo(() => {
    const enrollmentMfaType = mfaType || restrictToMfaType;

    if (enrollmentMfaType === AUTH0_MFA_TYPES.totp) {
      return {
        id: `${AUTH0_MFA_TYPES.totp}|'temp-id'`,
        type: AUTH0_MFA_TYPES.totp,
      };
    }

    return {
      id: `${verificationMethod}|temp-id`,
      type: enrollmentMfaType === MANAGEMENT_TYPES.PHONE ? MANAGEMENT_TYPES.PHONE : enrollmentMfaType,
      name: enrollmentName,
    };
  }, [enrollmentName, mfaType, restrictToMfaType, verificationMethod]);

  const onChallengePassed = useCallback(() => {
    if (isUnskippable && currentMfaType === MANAGEMENT_TYPES.TOTP) {
      dispatch(
        saveUserSetting({
          settingName: UserSettings.userHasVerifiedPhoneNumber.settingName,
          settingValue: `${PHONE_VERIFICATION_STATES.BYPASSED_VIA_OTP}-${new Date().toISOString().split('T')[0]}`,
          excludeV3prefix: true,
        }),
      );
    } else if (verificationMethod === AUTH0_MFA_TYPES.sms || verificationMethod === AUTH0_MFA_TYPES.voice) {
      dispatch(
        saveUserSetting({
          settingName: UserSettings.userHasVerifiedPhoneNumber.settingName,
          settingValue: `${PHONE_VERIFICATION_STATES.VERIFIED}-${new Date().toISOString().split('T')[0]}`,
          excludeV3prefix: true,
        }),
      );
    }

    onCloseModal();
    onSuccess();
  }, [isUnskippable, currentMfaType, verificationMethod, dispatch, onCloseModal, onSuccess]);

  // Create consent data with translated text for account owners
  const userIsAccountOwner = isOwner || isSystemAdmin;

  const initialConsentData = useMemo(() => {
    return !userIsAccountOwner
      ? undefined
      : CONSENT_MESSAGES.map((consent) => ({
          key: consent.messageKey,
          text: intl.formatMessage(messages[consent.messageKey]),
          accepted: false,
        }));
  }, [userIsAccountOwner, intl]);

  useEffect(() => {
    if (consents === undefined) {
      setConsents(initialConsentData);
    }
  }, [initialConsentData, consents]);

  return {
    phase,
    mfaType,
    error,
    verificationMethod,
    enrollmentValue,
    countryCode,
    consents,
    oobCode,
    enrollmentName,
    otpSecret,
    secretAsQrCodeUri,
    challengedEnrollment,
    accessToken,

    resetError,
    onCloseModal,
    onSelectMfaType,
    advanceToChallengePhase,
    onConsentChange,
    onStartVerification,
    onChallengePassed,
    setVerificationMethod,
    setEnrollmentValue,
    setCountryCode,

    createCompletePhoneNumber,

    PHASES,

    currentMfaType,
    switchMfaType,
    isUnskippable,
  };
};
